import {
  createSelector,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit"

import type { FavoriteMeeting, User } from "@/api/types"
import { logoutAction } from "@/store/session/logoutAction"
import type { RootState } from "@/store/store"

interface FavoritesState {
  callFavorites: User[]
  meetingFavorites: string[]
  meetingFavoriteDetails: Record<string, FavoriteMeeting>
}

interface SetFavoritesPayload {
  callFavorites: User[]
  meetingFavorites: FavoriteMeeting[]
}

const buildMeetingFavoriteDetails = (meetingFavorites: FavoriteMeeting[]) => {
  return Object.fromEntries(
    meetingFavorites.map((meeting) => [meeting.id, meeting]),
  ) as Record<string, FavoriteMeeting>
}

const initialState: FavoritesState = {
  callFavorites: [],
  meetingFavorites: [],
  meetingFavoriteDetails: {},
}

const favoritesSlice = createSlice({
  name: "favorites",
  initialState,
  reducers: {
    setFavorites: (state, action: PayloadAction<SetFavoritesPayload>) => {
      state.callFavorites = action.payload.callFavorites
      state.meetingFavorites = action.payload.meetingFavorites.map(
        (meeting) => meeting.id,
      )
      state.meetingFavoriteDetails = buildMeetingFavoriteDetails(
        action.payload.meetingFavorites,
      )
    },
    addCallFavorite: (state, action: PayloadAction<User>) => {
      const exists = state.callFavorites.some((f) => f.id === action.payload.id)
      if (!exists) {
        state.callFavorites = [action.payload, ...state.callFavorites]
      }
    },
    removeCallFavorite: (state, action: PayloadAction<string>) => {
      state.callFavorites = state.callFavorites.filter(
        (f) => f.id !== action.payload,
      )
    },
    addRoomFavorite: (
      state,
      action: PayloadAction<FavoriteMeeting | FavoriteMeeting["id"]>,
    ) => {
      const roomId =
        typeof action.payload === "string" ? action.payload : action.payload.id
      const exists = state.meetingFavorites.includes(roomId)

      if (typeof action.payload !== "string") {
        state.meetingFavoriteDetails[roomId] = action.payload
      }

      if (!exists) {
        state.meetingFavorites = [roomId, ...state.meetingFavorites]
      }
    },
    removeRoomFavorite: (state, action: PayloadAction<string>) => {
      state.meetingFavorites = state.meetingFavorites.filter(
        (roomId) => roomId !== action.payload,
      )
      delete state.meetingFavoriteDetails[action.payload]
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logoutAction, () => initialState)
  },
})

export const {
  setFavorites,
  addCallFavorite,
  removeCallFavorite,
  addRoomFavorite,
  removeRoomFavorite,
} = favoritesSlice.actions

export const selectCallFavorites = (state: RootState) =>
  state.favorites.callFavorites
export const selectRoomFavoriteIds = (state: RootState) =>
  state.favorites.meetingFavorites
const selectRoomFavoriteDetails = (state: RootState) =>
  state.favorites.meetingFavoriteDetails

export const selectRoomFavorites = createSelector(
  [selectRoomFavoriteIds, selectRoomFavoriteDetails],
  (roomFavoriteIds, roomFavoriteDetails) => {
    return roomFavoriteIds.flatMap((roomId) => {
      const favorite = roomFavoriteDetails[roomId]
      return favorite ? [favorite] : []
    })
  },
)

export const selectIsRoomFavorite = (roomId: string) =>
  createSelector(selectRoomFavoriteIds, (roomFavoriteIds) => {
    return roomFavoriteIds.includes(roomId)
  })

export default favoritesSlice.reducer
